import React, { useState, useEffect } from 'react';
import { api } from '../services/api';

interface BookingFormProps {
  isOpen: boolean;
  onClose: () => void;
}

const BookingForm: React.FC<BookingFormProps> = ({ isOpen, onClose }) => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventDate: '',
    eventType: '',
    soundProvided: false,
    estimatedGuests: '',
    indoorOutdoor: '',
    budget: '',
    desiredBandSize: '',
    message: ''
  });
  
  const [hasSpecificDate, setHasSpecificDate] = useState(false);
  const [hasKnownBudget, setHasKnownBudget] = useState(false);
  const [hasDesiredBandSize, setHasDesiredBandSize] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData({
        ...formData,
        [name]: (e.target as HTMLInputElement).checked
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear any previous error
    setErrorMessage('');
    
    // Validation
    if (!formData.name) {
      setErrorMessage('Name is required');
      return;
    }
    
    if (!formData.email && !formData.phone) {
      setErrorMessage('Either email or phone is required');
      return;
    }
    
    if (formData.email && !formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setErrorMessage('Please enter a valid email address');
      return;
    }
    
    if (formData.phone && !formData.phone.match(/^[\d\s()+-]+$/)) {
      setErrorMessage('Please enter a valid phone number');
      return;
    }
    
    if (!formData.message) {
      setErrorMessage('Additional information is required');
      return;
    }

    // Set submitting state
    setIsSubmitting(true);
    
    try {
      // Submit to API
      const response = await api.submitBooking({
        ...formData,
        desiredBandSize: formData.desiredBandSize || '5-Piece'
      });
      
      if (response.success) {
        // Show thank you message
        setShowThankYou(true);
        
        // Close after 3 seconds
        setTimeout(() => {
          onClose();
          setShowThankYou(false);
          // Reset form
          setFormData({
            name: '',
            email: '',
            phone: '',
            eventDate: '',
            eventType: '',
            soundProvided: false,
            estimatedGuests: '',
            indoorOutdoor: '',
            budget: '',
            desiredBandSize: '',
            message: ''
          });
          setHasSpecificDate(false);
          setHasKnownBudget(false);
          setHasDesiredBandSize(false);
          setErrorMessage('');
        }, 3000);
      } else {
        setErrorMessage('Failed to submit booking. Please try again.');
      }
    } catch (error: any) {
      console.error('Booking submission error:', error);
      
      // Check for rate limiting
      if (error.message && error.message.includes('Too many requests')) {
        setErrorMessage('Please wait a moment before submitting again.');
      } else {
        setErrorMessage('Failed to submit booking. Please try again or contact us directly.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Mobile: Full-screen form
  if (isMobile) {
    return (
      <div className="fixed inset-0 bg-black z-[100] overflow-y-auto">
        <div className="min-h-screen bg-gray-900">
          <div className="sticky top-0 bg-gray-900 z-10 p-4 border-b border-blue-900/30">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h2 className="text-2xl font-bold text-center mt-2" style={{ fontFamily: 'Oswald, sans-serif' }}>Booking Inquiry</h2>
          </div>

          <div className="p-6">
          {showThankYou ? (
            <div className="text-center py-20">
              <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>Thank You!</h2>
              <p className="text-xl text-gray-300">We will be in touch ASAP.</p>
              <p className="text-lg text-gray-400 mt-4">Across The Way</p>
            </div>
          ) : (
            <>
              <p className="text-gray-300 mb-6 text-center">Fill out the form below and we'll get back to you!</p>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Error Message */}
                {errorMessage && (
                  <div className="bg-red-900/50 border border-red-500 text-red-200 p-3 rounded">
                    {errorMessage}
                  </div>
                )}

                {/* Contact Details Section */}
                <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                  <h3 className="text-xl font-bold mb-4 text-white uppercase tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>Contact Details</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-200 mb-2 uppercase tracking-wide">Name *</label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:shadow-[0_0_10px_rgba(96,165,250,0.3)] transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-200 mb-2 uppercase tracking-wide">Email {!formData.phone ? '*' : ''}</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="your@email.com"
                          className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:shadow-[0_0_10px_rgba(96,165,250,0.3)] transition-all"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-200 mb-2 uppercase tracking-wide">Phone {!formData.email ? '*' : ''}</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(555) 555-5555"
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:shadow-[0_0_10px_rgba(96,165,250,0.3)] transition-all"
                      />
                    </div>
                  </div>
                </div>

                {/* Event Details Section */}
                <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                  <h3 className="text-xl font-bold mb-4 text-white uppercase tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>Event Details</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-200 mb-2 uppercase tracking-wide">Event Type</label>
                        <select
                          name="eventType"
                          value={formData.eventType}
                          onChange={handleChange}
                          className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:shadow-[0_0_10px_rgba(96,165,250,0.3)] transition-all"
                        >
                          <option value="">Select event type</option>
                          <option value="wedding">Wedding</option>
                          <option value="corporate">Corporate Event</option>
                          <option value="private-party">Private Party</option>
                          <option value="festival">Festival</option>
                          <option value="restaurant">Restaurant/Bar</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-200 mb-2 uppercase tracking-wide">Specific event date?</label>
                        <div className="flex gap-2 mb-3">
                          <button
                            type="button"
                            onClick={() => {
                              setHasSpecificDate(false);
                              setFormData({ ...formData, eventDate: '' });
                            }}
                            className={`flex-1 px-4 py-2 rounded font-medium transition-all ${
                              !hasSpecificDate
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            }`}
                          >
                            No
                          </button>
                          <button
                            type="button"
                            onClick={() => setHasSpecificDate(true)}
                            className={`flex-1 px-4 py-2 rounded font-medium transition-all ${
                              hasSpecificDate
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            }`}
                          >
                            Yes
                          </button>
                        </div>
                        {hasSpecificDate && (
                          <input
                            type="date"
                            name="eventDate"
                            value={formData.eventDate}
                            onChange={handleChange}
                            className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:shadow-[0_0_10px_rgba(96,165,250,0.3)] transition-all"
                          />
                        )}
                      </div>
                    </div>


                    <div>
                      <label className="block text-sm font-semibold text-gray-200 mb-2 uppercase tracking-wide">
                        Do We Need to Handle Sound?
                        <span className="inline-block ml-2 relative group cursor-help">
                          <svg className="w-4 h-4 text-blue-400 inline-block" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-2 bg-gray-800 text-gray-200 text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 border border-gray-600">
                            Does your venue provide PA speakers, a mixer and someone on site familiar with the gear?
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                              <div className="border-t-8 border-t-gray-800 border-x-8 border-x-transparent"></div>
                            </div>
                          </div>
                        </span>
                      </label>
                      <div className="flex gap-2 mb-3">
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, soundProvided: false });
                          }}
                          className={`flex-1 px-4 py-2 rounded font-medium transition-all ${
                            !formData.soundProvided
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          }`}
                        >
                          No
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, soundProvided: true })}
                          className={`flex-1 px-4 py-2 rounded font-medium transition-all ${
                            formData.soundProvided
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          }`}
                        >
                          Yes
                        </button>
                      </div>
                      {formData.soundProvided && (
                        <div className="space-y-3">
                          <input
                            type="text"
                            name="estimatedGuests"
                            value={formData.estimatedGuests}
                            onChange={handleChange}
                            placeholder="Estimated guests"
                            className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:shadow-[0_0_10px_rgba(96,165,250,0.3)] transition-all"
                          />
                          <select
                            name="indoorOutdoor"
                            value={formData.indoorOutdoor}
                            onChange={handleChange}
                            className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:shadow-[0_0_10px_rgba(96,165,250,0.3)] transition-all"
                          >
                            <option value="">Indoor/Outdoor</option>
                            <option value="indoor">Indoor</option>
                            <option value="outdoor">Outdoor</option>
                            <option value="both">Both</option>
                          </select>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-200 mb-2 uppercase tracking-wide">Known event budget?</label>
                      <div className="flex gap-2 mb-3">
                        <button
                          type="button"
                          onClick={() => {
                            setHasKnownBudget(false);
                            setFormData({ ...formData, budget: '' });
                          }}
                          className={`flex-1 px-4 py-2 rounded font-medium transition-all ${
                            !hasKnownBudget
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          }`}
                        >
                          No
                        </button>
                        <button
                          type="button"
                          onClick={() => setHasKnownBudget(true)}
                          className={`flex-1 px-4 py-2 rounded font-medium transition-all ${
                            hasKnownBudget
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          }`}
                        >
                          Yes
                        </button>
                      </div>
                      {hasKnownBudget && (
                        <input
                          type="text"
                          name="budget"
                          value={formData.budget}
                          onChange={handleChange}
                          placeholder="Enter budget (e.g., $2000 or $1500-2500)"
                          className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:shadow-[0_0_10px_rgba(96,165,250,0.3)] transition-all"
                        />
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-200 mb-2 uppercase tracking-wide">Desired Band Size?</label>
                      <div className="flex gap-2 mb-3">
                        <button
                          type="button"
                          onClick={() => {
                            setHasDesiredBandSize(false);
                            setFormData({ ...formData, desiredBandSize: '5-Piece' });
                          }}
                          className={`flex-1 px-4 py-2 rounded font-medium transition-all ${
                            !hasDesiredBandSize
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          }`}
                        >
                          5-Piece (Full Band)
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setHasDesiredBandSize(true);
                            setFormData({ ...formData, desiredBandSize: 'Duet/Trio' });
                          }}
                          className={`flex-1 px-4 py-2 rounded font-medium transition-all ${
                            hasDesiredBandSize
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          }`}
                        >
                          Duet/Trio
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Message Section */}
                <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                  <label className="block text-sm font-semibold text-gray-200 mb-2 uppercase tracking-wide">Additional Information *</label>
                  <textarea
                    name="message"
                    rows={3}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us more about your event..."
                    required
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:shadow-[0_0_10px_rgba(96,165,250,0.3)] transition-all"
                  />
                </div>

                <div className="flex flex-col gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded hover:from-blue-700 hover:to-purple-700 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Booking Inquiry'}
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="w-full px-6 py-2 text-gray-400 hover:text-white transition"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </>
          )}
          </div>
        </div>
      </div>
    );
  }

  // Desktop: Optimized modal
  return (
    <div className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="relative max-w-5xl w-full max-h-[90vh] bg-gray-900 rounded-lg booking-modal-border overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition z-[110] pointer-events-auto"
          type="button"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="flex-1 overflow-y-auto p-8">
          {showThankYou ? (
            <div className="text-center py-16">
              <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>Thank You!</h2>
              <p className="text-xl text-gray-300">We will be in touch ASAP.</p>
              <p className="text-lg text-gray-400 mt-4">Across The Way</p>
            </div>
          ) : (
            <>
              <h2 className="text-3xl font-bold mb-4 text-center" style={{ fontFamily: 'Oswald, sans-serif' }}>Across The Way - Booking Inquiry</h2>
              <p className="text-gray-300 mb-6 text-center">Fill out the form below and we'll get back to you about your event!</p>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Error Message */}
                {errorMessage && (
                  <div className="bg-red-900/50 border border-red-500 text-red-200 p-3 rounded">
                    {errorMessage}
                  </div>
                )}
                <div className="grid lg:grid-cols-2 gap-6">
                  {/* Contact Details Section */}
                  <div className="bg-gray-800 p-5 rounded-lg border border-gray-700">
                    <h3 className="text-xl font-bold mb-4 text-white uppercase tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>Contact Details</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-200 mb-2 uppercase tracking-wide">Name *</label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:shadow-[0_0_10px_rgba(96,165,250,0.3)] transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-200 mb-2 uppercase tracking-wide">Email {!formData.phone ? '*' : ''}</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="your@email.com"
                          className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:shadow-[0_0_10px_rgba(96,165,250,0.3)] transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-200 mb-2 uppercase tracking-wide">Phone {!formData.email ? '*' : ''}</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="(555) 555-5555"
                          className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:shadow-[0_0_10px_rgba(96,165,250,0.3)] transition-all"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Event Details Section */}
                  <div className="bg-gray-800 p-5 rounded-lg border border-gray-700">
                    <h3 className="text-xl font-bold mb-4 text-white uppercase tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>Event Details</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-200 mb-2 uppercase tracking-wide">Event Type</label>
                        <select
                          name="eventType"
                          value={formData.eventType}
                          onChange={handleChange}
                          className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:shadow-[0_0_10px_rgba(96,165,250,0.3)] transition-all"
                        >
                          <option value="">Select event type</option>
                          <option value="wedding">Wedding</option>
                          <option value="corporate">Corporate Event</option>
                          <option value="private-party">Private Party</option>
                          <option value="festival">Festival</option>
                          <option value="restaurant">Restaurant/Bar</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-200 mb-2 uppercase tracking-wide">Specific event date?</label>
                        <div className="flex gap-2 mb-3">
                          <button
                            type="button"
                            onClick={() => {
                              setHasSpecificDate(false);
                              setFormData({ ...formData, eventDate: '' });
                            }}
                            className={`flex-1 px-4 py-2 rounded font-medium transition-all ${
                              !hasSpecificDate
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            }`}
                          >
                            No
                          </button>
                          <button
                            type="button"
                            onClick={() => setHasSpecificDate(true)}
                            className={`flex-1 px-4 py-2 rounded font-medium transition-all ${
                              hasSpecificDate
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            }`}
                          >
                            Yes
                          </button>
                        </div>
                        {hasSpecificDate && (
                          <input
                            type="date"
                            name="eventDate"
                            value={formData.eventDate}
                            onChange={handleChange}
                            className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:shadow-[0_0_10px_rgba(96,165,250,0.3)] transition-all"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sound and Band Size/Budget Section */}
                <div className="grid lg:grid-cols-2 gap-6">
                  <div className="bg-gray-800 p-5 rounded-lg border border-gray-700">
                    <label className="block text-sm font-semibold text-gray-200 mb-3 uppercase tracking-wide">
                      Do We Need to Handle Sound?
                      <span className="inline-block ml-2 relative group cursor-help">
                        <svg className="w-4 h-4 text-blue-400 inline-block" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-2 bg-gray-800 text-gray-200 text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 border border-gray-600">
                          Does your venue provide PA speakers, a mixer and someone on site familiar with the gear?
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                            <div className="border-t-8 border-t-gray-800 border-x-8 border-x-transparent"></div>
                          </div>
                        </div>
                      </span>
                    </label>
                    <div className="flex gap-2 mb-3">
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, soundProvided: false });
                        }}
                        className={`flex-1 px-4 py-2 rounded font-medium transition-all ${
                          !formData.soundProvided
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        No
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, soundProvided: true })}
                        className={`flex-1 px-4 py-2 rounded font-medium transition-all ${
                          formData.soundProvided
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        Yes
                      </button>
                    </div>
                    {formData.soundProvided && (
                      <div className="space-y-3">
                        <input
                          type="text"
                          name="estimatedGuests"
                          value={formData.estimatedGuests}
                          onChange={handleChange}
                          placeholder="Estimated guests"
                          className="w-full px-4 py-2 bg-black/50 border border-blue-900/50 rounded text-white placeholder-gray-500 focus:border-blue-400 focus:outline-none focus:shadow-[0_0_10px_rgba(96,165,250,0.3)] transition-all text-sm"
                        />
                        <select
                          name="indoorOutdoor"
                          value={formData.indoorOutdoor}
                          onChange={handleChange}
                          className="w-full px-4 py-2 bg-black/50 border border-blue-900/50 rounded text-white focus:border-blue-400 focus:outline-none focus:shadow-[0_0_10px_rgba(96,165,250,0.3)] transition-all text-sm"
                        >
                          <option value="">Indoor/Outdoor</option>
                          <option value="indoor">Indoor</option>
                          <option value="outdoor">Outdoor</option>
                          <option value="both">Both</option>
                        </select>
                      </div>
                    )}
                  </div>

                  <div className="bg-gray-800 p-5 rounded-lg border border-gray-700">
                    {/* Desired Band Size */}
                    <div className="mb-6">
                      <label className="block text-sm font-semibold text-gray-200 mb-3 uppercase tracking-wide">Desired Band Size?</label>
                      <div className="flex gap-2 mb-3">
                        <button
                          type="button"
                          onClick={() => {
                            setHasDesiredBandSize(false);
                            setFormData({ ...formData, desiredBandSize: '5-Piece' });
                          }}
                          className={`flex-1 px-4 py-2 rounded font-medium transition-all ${
                            !hasDesiredBandSize
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          }`}
                        >
                          5-Piece (Full Band)
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setHasDesiredBandSize(true);
                            setFormData({ ...formData, desiredBandSize: 'Duet/Trio' });
                          }}
                          className={`flex-1 px-4 py-2 rounded font-medium transition-all ${
                            hasDesiredBandSize
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          }`}
                        >
                          Duet/Trio
                        </button>
                      </div>
                    </div>
                    
                    {/* Known Budget */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-200 mb-3 uppercase tracking-wide">Known budget?</label>
                      <div className="flex gap-2 mb-3">
                        <button
                          type="button"
                          onClick={() => {
                            setHasKnownBudget(false);
                            setFormData({ ...formData, budget: '' });
                          }}
                          className={`flex-1 px-4 py-2 rounded font-medium transition-all ${
                            !hasKnownBudget
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          }`}
                        >
                          No
                        </button>
                        <button
                          type="button"
                          onClick={() => setHasKnownBudget(true)}
                          className={`flex-1 px-4 py-2 rounded font-medium transition-all ${
                            hasKnownBudget
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          }`}
                        >
                          Yes
                        </button>
                      </div>
                      {hasKnownBudget && (
                        <input
                          type="text"
                          name="budget"
                          value={formData.budget}
                          onChange={handleChange}
                          placeholder="e.g., $2000 or $1500-2500"
                          className="w-full px-4 py-2 bg-black/50 border border-blue-900/50 rounded text-white placeholder-gray-500 focus:border-blue-400 focus:outline-none focus:shadow-[0_0_10px_rgba(96,165,250,0.3)] transition-all"
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* Message Section */}
                <div className="bg-gray-800 p-5 rounded-lg border border-gray-700">
                  <label className="block text-sm font-semibold text-gray-200 mb-2 uppercase tracking-wide">Additional Information *</label>
                  <textarea
                    name="message"
                    rows={3}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us more about your event..."
                    required
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:shadow-[0_0_10px_rgba(96,165,250,0.3)] transition-all"
                  />
                </div>

                <div className="flex justify-end space-x-4 pt-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-6 py-2 text-gray-400 hover:text-white transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded hover:from-blue-700 hover:to-purple-700 transition font-semibold space-glow disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Booking Inquiry'}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingForm;